/**
 * Pure work-report content helpers — the validation + normalisation logic
 * that gates every employee's Daily / Weekly / Monthly report submission.
 * Extracted from `pages/employee/ReportsPage.jsx` so they can be unit-tested
 * directly (a `.jsx` page can't be imported by `node --test`); the page
 * imports these verbatim, so behaviour is unchanged.
 *
 * A report `content` object has: summary, notes (strings); workItems,
 * blockers, meetings (arrays of row objects); accomplishments, plansForNext
 * (arrays of strings); taskIds (mirror of linked work-item task ids).
 */

/**
 * Drop blank placeholder rows before submit. Every "Add row / blocker /
 * meeting / bullet" inserts an empty row; the server's Zod schema rejects a
 * report with a blank-title work item / blank blocker / blank meeting title,
 * so stripping untouched rows here means a forgotten placeholder never blocks
 * submission. `taskIds` is rebuilt from the surviving linked work items.
 */
export function cleanContentForSubmit(content) {
  const nonBlank = (s) => (s ?? '').trim().length > 0
  const workItems = (content.workItems ?? []).filter((it) => nonBlank(it.title))
  const blockers = (content.blockers ?? []).filter((b) => nonBlank(b.blocker))
  const meetings = (content.meetings ?? []).filter((m) => nonBlank(m.title))
  return {
    ...content,
    workItems,
    blockers,
    meetings,
    accomplishments: (content.accomplishments ?? []).filter(nonBlank),
    plansForNext: (content.plansForNext ?? []).filter(nonBlank),
    taskIds: workItems.map((it) => it.taskId).filter(Boolean),
  }
}

/** True when a report carries no real content once placeholder rows are
 *  dropped — weekly/monthly block submission on it (the server enforces the
 *  same rule via reports.dto `reportHasContent`). */
export function reportContentIsEmpty(content) {
  const c = cleanContentForSubmit(content)
  const has = (s) => (s ?? '').trim().length > 0
  return (
    !has(c.summary) &&
    !has(c.notes) &&
    (c.workItems?.length ?? 0) === 0 &&
    (c.accomplishments?.length ?? 0) === 0 &&
    (c.blockers?.length ?? 0) === 0 &&
    (c.plansForNext?.length ?? 0) === 0 &&
    (c.meetings?.length ?? 0) === 0
  )
}

/** A work row that carries data (hours, project, module or description) but
 *  no task title. `cleanContentForSubmit` drops blank-title rows, so such a
 *  row would be silently discarded on submit — losing the logged hours — so
 *  it's surfaced inline and blocked at submit. A fully-empty placeholder row
 *  is NOT flagged (it's droppable as intended). */
export function workRowMissingTitle(it) {
  if (!it) return false
  return (
    (it.title ?? '').trim().length === 0 &&
    (Number(it.timeSpent ?? 0) > 0 ||
      Boolean((it.project ?? '').trim()) ||
      Boolean((it.module ?? '').trim()) ||
      Boolean((it.description ?? '').trim()))
  )
}

/** A blocker row that carries data (a dependency, or a status moved off the
 *  default "open") but no blocker text — would be silently dropped on submit.
 *  A fully untouched placeholder (no text, no dependency, status still open)
 *  stays droppable and is not flagged. */
export function blockerRowMissingText(b) {
  if (!b || typeof b === 'string') return false
  return (
    (b.blocker ?? '').trim().length === 0 &&
    (Boolean((b.dependencyOn ?? '').trim()) ||
      (b.resolutionStatus ?? 'open') !== 'open')
  )
}

/** Stable signature of a report's user-editable content, normalised so two
 *  reports that differ only by blank rows / whitespace / server-computed
 *  fields (hoursLogged, taskIds) compare equal — used to detect a "re-submit
 *  with no changes". */
export function reportContentSig(c) {
  const txt = (s) => (s ?? '').trim()
  return JSON.stringify({
    summary: txt(c?.summary),
    notes: txt(c?.notes),
    workItems: (c?.workItems ?? [])
      .filter((it) => txt(it.title))
      .map((it) => ({
        title: txt(it.title),
        description: txt(it.description),
        project: txt(it.project),
        module: txt(it.module),
        type: it.type,
        status: it.status,
        priority: it.priority,
        timeSpent: Number(it.timeSpent ?? 0),
      })),
    accomplishments: (c?.accomplishments ?? []).map(txt).filter(Boolean),
    blockers: (c?.blockers ?? [])
      .filter((b) => txt(b.blocker))
      .map((b) => ({
        blocker: txt(b.blocker),
        dependencyOn: txt(b.dependencyOn),
        resolutionStatus: b.resolutionStatus,
      })),
    plansForNext: (c?.plansForNext ?? []).map(txt).filter(Boolean),
    meetings: (c?.meetings ?? [])
      .filter((m) => txt(m.title))
      .map((m) => ({
        title: txt(m.title),
        purpose: txt(m.purpose),
        durationMinutes: Number(m.durationMinutes ?? 0),
        attendees: txt(m.attendees),
      })),
  })
}
